import { QuoteGetResponse } from '@lio/api-types';
import { useEffect, useMemo } from 'react';
import { QuoteStatusChangeItem } from '@models/quoteStatusChange';
import { PortalMessage } from '@components/portalMessage/PortalMessage';
import Loading from '@components/Loading';
import { useSearchQuoteStatusChange } from '@services/quotes';
import { QuoteStatus } from '@models/quotes';
import { DownloadApplicationButton, yellowTextButtonStyles } from '@components/button/DownloadApplicationButton';
import { Box } from '@mui/material';
import { PortalMessageType } from '@components/portalMessage/PortalMessageType.enum';

export const DeclinedQuote = ({ quote }: { quote: QuoteGetResponse }) => {
  const { quoteStatusChange, fetchNextPage } = useSearchQuoteStatusChange({
    quoteId: quote.id,
    pageLimit: 50,
  });
  const allStatusChanges = quoteStatusChange?.pages.flatMap<QuoteStatusChangeItem>((page) => page.items);
  const reasons = useMemo(() => {
    if (allStatusChanges) {
      return allStatusChanges
        ?.filter((el) => el.newQuoteStatus.id === QuoteStatus.Declined.id)
        .reduce((arr, item) => {
          if (item.quoteStatusChangeReason?.reason) {
            arr.push(item.quoteStatusChangeReason.reason);
          }
          return arr;
        }, [] as string[]);
    }
    return [];
  }, [allStatusChanges]);

  useEffect(() => {
    const totalItems = quoteStatusChange?.pages[0].pagination.total ?? 0;
    const currentTotalItems = allStatusChanges?.length ?? 0;
    if (totalItems > currentTotalItems) {
      fetchNextPage();
    }
  }, [fetchNextPage, quoteStatusChange, allStatusChanges]);

  if (!quote) {
    return <Loading />;
  }

  const secondarySubTitleArray =
    reasons.length > 0
      ? []
      : [<span key='question-text'>If you have questions, please contact your LIO Underwriter.</span>];

  secondarySubTitleArray.push(
    <Box component='span' sx={{ display: 'flex' }}>
      You may
      <DownloadApplicationButton
        label='download your application'
        quoteId={quote.id}
        buttonProps={{
          color: 'secondary',
          variant: 'text',
          disableRipple: true,
          sx: yellowTextButtonStyles,
        }}
        quoteUpdatedDate={quote?.updatedAt}
      />
      for your records.
    </Box>,
  );
  return (
    <PortalMessage
      type={PortalMessageType.Declined}
      title={["Sorry, we can't quote this."]}
      subtitle={[
        <span key='declined-text'>
          {`This quote has been declined by LIO Underwriters${
            reasons.length > 0 ? ' for the following reason(s):' : ''
          }`}
        </span>,
      ]}
      secondarySubtitle={secondarySubTitleArray}
      primaryBtnText='View Messages'
      primaryBtnAction={`/quote/${quote.id}?viewReadOnly=True&sidebarItem=messages`}
      secondaryBtnText='Continue to your Homepage'
      secondaryBtnAction='/'
      items={reasons}
      quote={quote}
    />
  );
};
